<template>
  <!-- Right Panel: Side Panel (Collapsible) -->
  <div class="right-panel side-panel" :class="{ collapsed: isCollapsed }" :style="{ width: panelWidth }">
    <button class="panel-collapse-btn" @click="togglePanel">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" :class="{ 'rotate-180': !isCollapsed }">
        <path d="M10 4L6 8L10 12" stroke="#374957" stroke-width="2"/>
      </svg>
    </button>

    <div v-if="!isCollapsed" class="side-panel-content">
      <!-- Side Panel Tabs -->
      <div class="side-panel-tabs">
        <Tab
          v-model="activeTab"
          type="underline"
          color="vibrant-dark"
          :tabs="tabItems"
          @change="handleTabChange"
        />
      </div>

      <!-- Side Panel Content -->
      <div class="side-panel-body">
        <!-- Block Editor Content -->
        <div v-if="activeTab === 'editor'">
          <!-- Empty State -->
          <div v-if="!selectedNode" class="editor-empty-state">
            <div class="empty-icon">
              <img src="@/assets/MyWorkBench/Workflow/block_default.svg" alt="Empty State">
            </div>
            <div class="empty-text">
              <p class="empty-title">No building block selected.</p>
              <p class="empty-description">Select a building block from the canvas to set its properties here.</p>
            </div>
          </div>

          <!-- Node Editor -->
          <div v-else>
            <TriggerStatusUpdateEditor
              v-if="getEditorType(selectedNode) === 'statusUpdate'"
              :trigger-type="getTriggerType(selectedNode)"
              :initial-events="selectedNode.data.events || []"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <TriggerManualEditor
              v-else-if="getEditorType(selectedNode) === 'manual'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <TriggerScheduledEditor
              v-else-if="getEditorType(selectedNode) === 'scheduled'"
              :data="selectedNode.data"
              :is-program="isProgram"
              @save="handleSaveConfiguration"
            />
            <ActionSendMessageEditor
              v-else-if="getEditorType(selectedNode) === 'sendMessage'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <ActionAppointmentEditor
              v-else-if="getEditorType(selectedNode) === 'appointment'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <ActionAssignTaskEditor
              v-else-if="getEditorType(selectedNode) === 'assignTask'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <ActionSendDocumentsEditor
              v-else-if="getEditorType(selectedNode) === 'sendDocuments'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <ActionAddInternalNotesEditor
              v-else-if="getEditorType(selectedNode) === 'addInternalNotes'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <ActionPlaceOrderEditor
              v-else-if="getEditorType(selectedNode) === 'placeOrder'"
              :data="selectedNode.data"
              @save="handleSaveConfiguration"
            />
            <LogicWaitEditor
              v-else-if="getEditorType(selectedNode) === 'wait'"
              :data="selectedNode.data"
              @save-configuration="handleSaveConfiguration"
              @remove-block="handleRemoveBlock"
            />
            <LogicReviewApprovalEditor
              v-else-if="getEditorType(selectedNode) === 'review'"
              :data="selectedNode.data"
              @save-configuration="handleSaveConfiguration"
              @remove-block="handleRemoveBlock"
            />
          </div>
        </div>

        <!-- AI Assistant -->
        <div v-if="activeTab === 'ai'" class="ai-assistant">
          <div ref="chatArea" class="ai-chat-area">
            <!-- Initial AI Message -->
            <AIMessageBubble
              type="ai"
              content="Tell me what you need. I can help you drafting a workflow."
            />

            <!-- Chat Messages -->
            <AIMessageBubble
              v-for="(message, index) in chatMessages"
              :key="index"
              :type="message.type"
              :content="message.content"
              :loading="message.loading"
              :loading-state="message.loadingState"
              :error="message.error"
              :show-action="message.showAction"
            />
          </div>
          
          <!-- Error Notification (positioned above input) -->
          <AIErrorNotification
            :visible="showErrorNotification"
            :message="errorMessage"
            :show-action="showErrorAction"
            @action-click="sendMessage"
          />
          
          <div class="ai-input-area">
            <div class="ai-input-wrapper" :class="{ 'disabled-pointer': isGenerating || hasAssignees }">
              <textarea 
                v-model="userInput"
                class="ai-input" 
                placeholder="Send the patient a bill too when the order is completed."
                rows="3"
                :disabled="isGenerating || hasAssignees"
                @keydown.enter.ctrl="sendMessage"
              ></textarea>
              <button
                class="ai-send-btn"
                :disabled="!userInput.trim() || isGenerating || hasAssignees"
                @click="sendMessage"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M1.5 16.5L16.5 9L1.5 1.5L1.5 7.5L11.5 9L1.5 10.5L1.5 16.5Z" fill="white"/>
                </svg>
                <span>{{ isGenerating ? 'Thinking...' : 'Send' }}</span>
              </button>
            </div>
          </div>
        </div>
        <!-- Program Settings -->
        <div v-if="activeTab === 'programSettings'" class="program-settings-container">
          <ProgramSettingsContent
            :value="programSettings"
            :has-assignees="hasAssignees"
            :total-order-cost="totalOrderCost"
            @save="handleProgramSettingsSave"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TriggerStatusUpdateEditor from './Editors/TriggerStatusUpdateEditor.vue';
import TriggerManualEditor from './Editors/TriggerManualEditor.vue';
import TriggerScheduledEditor from './Editors/TriggerScheduledEditor.vue';
import ActionSendMessageEditor from './Editors/ActionSendMessageEditor.vue';
import ActionAppointmentEditor from './Editors/ActionAppointmentEditor.vue';
import ActionAssignTaskEditor from './Editors/ActionAssignTaskEditor.vue';
import ActionSendDocumentsEditor from './Editors/ActionSendDocumentsEditor.vue';
import ActionAddInternalNotesEditor from './Editors/ActionAddInternalNotesEditor.vue';
import ActionPlaceOrderEditor from './Editors/ActionPlaceOrderEditor.vue';
import LogicWaitEditor from './Editors/LogicWaitEditor.vue';
import LogicReviewApprovalEditor from './Editors/LogicReviewApprovalEditor.vue';
import ProgramSettingsContent from './ProgramSettingsContent.vue';
import AIMessageBubble from './AIMessageBubble.vue';
import AIErrorNotification from './AIErrorNotification.vue';
import { generateWorkflowWithAIApi, transformWorkflowData } from '@/api/workflowManage';
import tabBlockEditorIcon from '@/assets/MyWorkBench/Workflow/tab_block_editor.svg';
import tabProgramSettingsIcon from '@/assets/MyWorkBench/Workflow/tab_program_settings.svg';
import tabAiAssistantIcon from '@/assets/MyWorkBench/Workflow/tab_ai_assistant.svg';

export default {
  name: 'CanvasRightPanel',
  components: {
    TriggerStatusUpdateEditor,
    TriggerManualEditor,
    TriggerScheduledEditor,
    ActionSendMessageEditor,
    ActionAppointmentEditor,
    ActionAssignTaskEditor,
    ActionSendDocumentsEditor,
    ActionAddInternalNotesEditor,
    ActionPlaceOrderEditor,
    LogicWaitEditor,
    LogicReviewApprovalEditor,
    ProgramSettingsContent,
    AIMessageBubble,
    AIErrorNotification
  },
  props: {
    selectedNode: {
      type: Object,
      default: null
    },
    hasAssignees: {
      type: Boolean,
      default: false
    },
    nodes: {
      type: Array,
      default: () => []
    },
    edges: {
      type: Array,
      default: () => []
    },
    savedConfigurations: {
      type: Array,
      default: () => []
    },
    workflowTitle: {
      type: String,
      default: ''
    },
    workflowDescription: {
      type: String,
      default: ''
    },
    showPreview: {
      type: Boolean,
      default: false
    },
    isProgram: {
      type: Boolean,
      default: true
    },
    programSettings: {
      type: Object,
      default: () => ({
        name: '',
        description: '',
        duration_type: '',
        start_date: '',
        end_date: '',
        durationValue: '',
        durationUnit: '',
        price: 0
      })
    },
    totalOrderCost: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isCollapsed: false,
      activeTab: 'editor',
      userInput: '',
      chatMessages: [],
      isGenerating: false,
      showErrorNotification: false,
      errorMessage: '',
      showErrorAction: false,
      lastUserMessage: ''
    };
  },
  computed: {
    panelWidth() {
      if (this.isCollapsed) {
        return '40px';
      }
      return this.isProgram ? '520px' : '324px';
    },
    tabItems() {
      const tabs = [
        { value: 'editor', label: 'Block Editor', icon: tabBlockEditorIcon },
      ];
      if (this.isProgram) {
        tabs.push({ value: 'programSettings', label: 'Program Settings', icon: tabProgramSettingsIcon });
      }
      tabs.push({ value: 'ai', label: 'AI Assistant', icon: tabAiAssistantIcon });
      return tabs;
    },
  },
  watch: {
    selectedNode(newNode) {
      // When a node is selected for editing, automatically switch to Editor tab
      // But block this action during AI generation or preview
      if (newNode && !this.isGenerating && !this.showPreview) {
        this.activeTab = 'editor';
      }
    },
    isGenerating(newVal) {
      // When AI generation starts, force switch to AI tab
      if (newVal) {
        this.activeTab = 'ai';
      }
    },
    showPreview(newVal) {
      // When preview mode starts, force switch to AI tab
      if (newVal) {
        this.activeTab = 'ai';
      }
      // When preview mode ends, reset isGenerating state
      if (!newVal) {
        this.isGenerating = false;
      }
    }
  },
  methods: {
    togglePanel() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('toggle', !this.isCollapsed);
    },

    handleTabChange(value) {
      if ((value === 'editor' || value === 'programSettings') && (this.isGenerating || this.showPreview)) {
        // Revert to ai tab if blocked
        this.$nextTick(() => { this.activeTab = 'ai'; });
      }
    },

    switchToEditorTab() {
      // Block switching to editor tab during AI generation or preview
      if (this.isGenerating || this.showPreview) {
        return;
      }
      // Expand panel if collapsed when opening editor
      if (this.isCollapsed) {
        this.isCollapsed = false;
        this.$emit('toggle', false);
      }
      this.activeTab = 'editor';
    },
    
    getEditorType(node) {
      if (!node || !node.type) return null;
      
      // Map backend enum trigger types to editor types
      const triggerTypes = ['order_update', 'appointment_update', 'document_update', 
                           'report_update', 'task_update', 'internal_notes_update', 'new_patient_created'];
      if (triggerTypes.includes(node.type)) {
        return 'statusUpdate';
      }
      
      if (node.type === 'manual') {
        return 'manual';
      }
      
      if (node.type === 'scheduled') {
        return 'scheduled';
      }
      
      if (node.type === 'send_message') {
        return 'sendMessage';
      }
      
      if (node.type === 'make_appointment') {
        return 'appointment';
      }
      
      if (node.type === 'assign_task') {
        return 'assignTask';
      }
      
      if (node.type === 'send_document') {
        return 'sendDocuments';
      }
      
      if (node.type === 'place_order') {
        return 'placeOrder';
      }
      
      if (node.type === 'add_internal_notes') {
        return 'addInternalNotes';
      }
      
      if (node.type === 'wait') {
        return 'wait';
      }
      
      if (node.type === 'review') {
        return 'review';
      }

      return null;
    },
    
    getTriggerType(node) {
      if (!node || !node.type) return 'orderUpdate';
      
      // Map backend enum values to editor triggerType
      const triggerTypeMap = {
        'order_update': 'orderUpdate',
        'appointment_update': 'appointmentUpdate',
        'document_update': 'documentUpdate',
        'report_update': 'reportUpdate',
        'task_update': 'taskUpdate',
        'internal_notes_update': 'profileUpdate',
        'new_patient_created': 'newPatientCreated'
      };
      
      return triggerTypeMap[node.type] || 'orderUpdate';
    },
    
    handleSaveConfiguration(configData) {
      this.$emit('save-configuration', {
        nodeId: this.selectedNode.id,
        data: configData
      });
    },

    handleRemoveBlock() {
      // Emit remove-block event with the edge ID (selectedNode.id for logic blocks)
      this.$emit('remove-block', this.selectedNode.id);
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isGenerating) return;

      const userMessage = this.userInput.trim();

      // Clear error notification and emit generation start (for blocking, not overlay)
      this.showErrorNotification = false;
      this.$emit('ai-generation-start');

      // Add user message to chat
      this.chatMessages.push({
        type: 'user',
        content: userMessage,
        loading: false
      });

      this.userInput = '';

      // Add thinking AI message (three dot animation)
      this.chatMessages.push({
        type: 'ai',
        content: '',
        loading: true,
        loadingState: 'thinking'
      });

      this.isGenerating = true;
      this.scrollToBottom();

      try {
        // Prepare existing workflow data using the same format as when saving to backend
        let existingWorkflow = null;
        if (this.nodes.length > 0) {
          // Combine nodes with saved configurations
          const finalNodes = this.nodes.map(node => {
            const savedConfig = this.savedConfigurations.find(
              config => config.type === 'node' && config.id === node.id
            );

            if (savedConfig) {
              return {
                ...node,
                data: savedConfig.data
              };
            }

            return node;
          });

          // Combine edges with saved configurations
          const finalEdges = this.edges.map(edge => {
            const savedConfig = this.savedConfigurations.find(
              config => config.type === 'edge' && config.id === edge.id
            );

            if (savedConfig) {
              return {
                ...edge,
                data: savedConfig.data
              };
            }

            return edge;
          });

          // Transform to backend format using the same function as save
          const backendData = transformWorkflowData({
            workflowTitle: this.workflowTitle || 'Untitled Workflow',
            workflowDescription: this.workflowDescription || '',
            nodes: finalNodes,
            edges: finalEdges,
            savedConfigurations: this.savedConfigurations
          });

          // Extract the workflow definition (nodes and connections)
          existingWorkflow = {
            nodes: backendData.definition.nodes,
            connections: backendData.definition.connections
          };
        }

        // Call AI API with existing workflow in backend format
        const response = await generateWorkflowWithAIApi(userMessage, existingWorkflow, this.isProgram);

        // Check if the response indicates success
        if (response.data && response.data.success) {
          // Change from thinking to generating in chat
          this.chatMessages[this.chatMessages.length - 1].loadingState = 'generating';
          this.scrollToBottom();

          // NOW show the AI overlay for the fake "generating" phase
          this.$emit('ai-generating-overlay-show');

          // Fake generating delay (random 0.5 to 1.5s)
          const generatingDelay = Math.random() * 1000 + 500;
          await new Promise(resolve => setTimeout(resolve, generatingDelay));

          // Remove generating message
          this.chatMessages.pop();

          // Add AI response message from API
          this.chatMessages.push({
            type: 'ai',
            content: response.data.message || 'Workflow generated successfully!',
            loading: false,
            error: false
          });

          // Emit event to parent component with generated workflow data
          // This will show the preview and close the overlay
          this.$emit('ai-workflow-generated', response.data.workflow);
        } else {
          // Remove loading message
          this.chatMessages.pop();

          // Handle API success but workflow generation failure - show error as AI message in chat
          this.chatMessages.push({
            type: 'ai',
            content: response.data?.error || response.data?.message || 'Failed to generate workflow. Please try again.',
            loading: false
          });

          // End generation state (no overlay was shown)
          this.isGenerating = false;
          this.$emit('ai-generation-end');
        }

      } catch (error) {
        console.error('AI workflow generation error:', error);

        // Remove loading message
        this.chatMessages.pop();

        // Show error notification without restoring user input
        this.errorMessage = error.response?.data?.message || 'Internet connection is weak. Please try again later.';
        this.showErrorAction = false;
        this.showErrorNotification = true;

        // End generation state (no overlay was shown)
        this.isGenerating = false;
        this.$emit('ai-generation-end');
      }

      this.scrollToBottom();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatArea = this.$refs.chatArea;
        if (chatArea) {
          chatArea.scrollTop = chatArea.scrollHeight;
        }
      });
    },

    handleProgramSettingsSave(data) {
      console.log('Program Settings saved:', data);
      this.$emit('save-program-settings', data);
    }
  }
};
</script>

<style scoped>
/* Right Panel - Side Panel */
.right-panel {
  height: 100%;
  background: #F9FBFD;
  border-left: 1px solid #E6E6E6;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  position: relative;
  transition: width 0.3s;
  z-index: 50;
}

.right-panel.collapsed {
  width: 40px !important;
}
.panel-collapse-btn {
  position: absolute;
  top: 50%;
  left: -12px;
  transform: translateY(-50%);
  width: 24px;
  height: 48px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 4px 0 0 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.panel-collapse-btn:hover {
  background: #F3F4F6;
}

.side-panel-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.side-panel-tabs {
  width: 100%;
  background: #FFFFFF;
  padding-top: 8px;
}

.side-panel-tabs :deep(.va-tab-group) {
  width: 100%;
  background: #FFFFFF;
  border-bottom: 1px solid #E6E6E6;
}

.side-panel-tabs :deep(.va-tab-group__main) {
  width: 100%;
  display: flex;
}

.side-panel-tabs :deep(.va-tab) {
  flex: 1;
}


.side-panel-body {
  flex: 1;
  min-height: 0;
  padding: 16px 16px 32px 16px; /* Reduced right padding to account for scrollbar */
  overflow-y: auto;
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(196, 196, 196, 0.5) transparent; /* Firefox */
}

/* Webkit scrollbar styling for right panel */
.side-panel-body::-webkit-scrollbar {
  width: 8px;
}

.side-panel-body::-webkit-scrollbar-track {
  background: transparent;
}

.side-panel-body::-webkit-scrollbar-thumb {
  background: rgba(196, 196, 196, 0.5);
  border-radius: 4px;
}

.side-panel-body::-webkit-scrollbar-thumb:hover {
  background: rgba(153, 153, 153, 0.8);
}

/* Editor Empty State */
.editor-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 64px;
}

.empty-icon {
  width: 108px;
  height: 104px;
}

.empty-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.empty-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666;
  margin: 0;
}

.empty-description {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #666;
  margin: 0;
}

/* AI Assistant */
.ai-assistant {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.ai-chat-area {
  flex: 1;
  padding: 16px 8px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ai-input-area {
  padding: 10px;
  border-top: 1px solid #E6E6E6;
}

.ai-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 8px;
}

.ai-input {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  min-height: 60px;
}

.ai-input::placeholder {
  color: #999;
}

.ai-send-btn {
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: #C4C4C4;
  border: none;
  border-radius: 15px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #FFFFFF;
  cursor: pointer;
  transition: background 0.2s;
}

.ai-send-btn:not(:disabled) {
  background: #004879;
}

.ai-send-btn:not(:disabled):hover {
  background: #003366;
}

.ai-send-btn:disabled {
  background: #C4C4C4;
  cursor: not-allowed;
}

.ai-send-btn svg {
  width: 18px;
  height: 18px;
}
.disabled-pointer {
  cursor: not-allowed;
}
.ai-input:disabled {
  cursor: not-allowed;
  background-color: #ffffff
}

/* Program Settings Container */
.program-settings-container {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
}
</style>
